import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Eye,
  EyeOff,
  Headset,
  Loader2,
  Lock,
  Mail,
} from "lucide-react";
import ServiceDeskAPIService from "@sd/services/APIs";
import { useAuth } from "@sd/hooks/useAuth";
import {
  isValidAccessToken,
  notifySdAuthChanged,
  extractAuthTokens,
  extractAuthUser,
} from "@sd/lib/sdAuthSession";
import {
  resolveSdPostLoginPath,
} from "@sd/lib/sdReturnPath";
import type { SdAppRole } from "@sd/lib/sdPaths";
import {
  setLocalAccessToken,
  setLocalRefreshToken,
  setLocalUserInfo,
} from "@/lib/storeManager";

type AuthBody = Record<string, unknown>;

function resolveSdRoleFromUserRaw(userRaw: unknown): SdAppRole | null {
  if (!userRaw || typeof userRaw !== "object") return null;
  const u = userRaw as Record<string, unknown>;
  const candidates = [u.role, u.service_desk_role, u.sd_role];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (
      s === "ServiceDesk_Admin" ||
      s === "ServiceDesk_Agent" ||
      s === "ServiceDesk_SuperAgent" ||
      s === "ServiceDesk_User"
    ) {
      return s;
    }
  }
  return null;
}

function extractTokens(body: AuthBody): {
  access: string | null;
  refresh: string | null;
  userRaw: unknown;
} {
  const { access, refresh } = extractAuthTokens(body);
  return { access, refresh, userRaw: extractAuthUser(body) };
}

const inputClassName =
  "h-12 w-full rounded-xl border border-slate-200 bg-[#eff6ff] pl-11 pr-4 text-[15px] text-slate-900 " +
  "placeholder:text-slate-400 outline-none transition-all duration-200 " +
  "hover:border-slate-300 hover:bg-white focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-500/10";

const labelClassName =
  "mb-2 block text-[13px] font-medium text-slate-600";

const primaryButtonClassName =
  "mt-1 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-blue-600 " +
  "px-4 text-[15px] font-semibold text-white transition-all duration-200 " +
  "hover:bg-blue-700 active:scale-[0.99] " +
  "disabled:pointer-events-none disabled:opacity-60";

export default function SDLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { refetchRole, role, isLoading: authLoading } = useAuth();

  useEffect(() => {
    if (!isValidAccessToken() || authLoading) return;
    navigate(
      resolveSdPostLoginPath(role, location.state?.from),
      { replace: true }
    );
  }, [navigate, location.state, role, authLoading]);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const applySessionFromBody = async (body: AuthBody): Promise<boolean> => {
    const { access, refresh, userRaw } = extractTokens(body);
    if (!access || !refresh) {
      return false;
    }
    setLocalAccessToken(access);
    setLocalRefreshToken(refresh);
    if (userRaw && typeof userRaw === "object") {
      const u = userRaw as Record<string, unknown>;
      setLocalUserInfo({
        ...u,
        id: (u.id ?? u.user_id) as string | undefined,
      });
    }
    notifySdAuthChanged();
    const loginRole = resolveSdRoleFromUserRaw(userRaw);
    await refetchRole();
    queryClient.clear();
    navigate(
      resolveSdPostLoginPath(loginRole ?? role, location.state?.from),
      { replace: true }
    );
    return true;
  };

  const parseApiError = (err: unknown): string => {
    const ax = err as { response?: { data?: { message?: string; error?: string } } };
    const msg =
      ax?.response?.data?.message ||
      ax?.response?.data?.error ||
      "Something went wrong. Please try again.";
    return typeof msg === "string" ? msg : "Something went wrong. Please try again.";
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await ServiceDeskAPIService.auth.login({ email, password });
      const body = response.data as AuthBody;
      const ok = await applySessionFromBody(body);
      if (!ok) {
        setError("Login failed. Please check your credentials.");
      }
    } catch (err: unknown) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full overflow-hidden bg-[#dce9f6]">
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.22]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgb(51 65 85 / 0.3) 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_75%_at_50%_42%,#f0f7ff_0%,#dce9f6_72%)]" />
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_-10%,rgba(37,99,235,0.12),transparent_55%)]" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/20 via-transparent to-slate-200/15" />

      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12 sm:px-6">
        <div className="w-full max-w-[420px]">
          <header className="mb-7 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-600 text-white">
              <Headset className="h-7 w-7" strokeWidth={1.75} aria-hidden />
            </div>
            <h1 className="mt-5 text-[1.65rem] font-bold tracking-tight text-slate-900">
              ServiceDesk
            </h1>
            <p className="mt-1.5 text-sm text-blue-950/50">
              IT Service Management Platform
            </p>
          </header>

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
            <div className="h-0.5 w-full bg-blue-600" aria-hidden />
            <div className="p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-lg font-semibold text-slate-900">Welcome back</h2>
              <p className="mt-1 text-sm text-slate-500">
                Sign in to your account
              </p>
            </div>

            {error && (
                <div
                  className="mb-5 flex gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3.5 text-sm text-red-800"
                  role="alert"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden />
                  <p className="leading-relaxed">{error}</p>
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label htmlFor="sd-email" className={labelClassName}>
                    Email address
                  </label>
                  <div className="relative">
                    <Mail
                      className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <input
                      id="sd-email"
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      autoComplete="email"
                      placeholder="name@company.com"
                      className={inputClassName}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="sd-password" className={labelClassName}>
                    Password
                  </label>
                  <div className="relative">
                    <Lock
                      className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400"
                      strokeWidth={1.75}
                      aria-hidden
                    />
                    <input
                      id="sd-password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      autoComplete="current-password"
                      placeholder="Enter your password"
                      className={`${inputClassName} pr-12`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(p => !p)}
                      className="absolute right-1 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.75} />
                      ) : (
                        <Eye className="h-[18px] w-[18px]" strokeWidth={1.75} />
                      )}
                    </button>
                  </div>
                </div>

                <button type="submit" disabled={loading} className={primaryButtonClassName}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      Signing in…
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              </form>
            </div>
          </div>

          <p className="mt-6 text-center text-xs text-slate-400">
            © {new Date().getFullYear()} Universal Software
          </p>
        </div>
      </div>
    </div>
  );
}
